// ─── Viewport predicate ───
//
// One definition of "mobile" for the whole app. `768` is Tailwind's `md`, which is what the
// sidebar drawer's backdrop already uses (`md:hidden`) — the drawer's three dismissal routes
// (backdrop tap, Escape, and choosing a destination) have to agree about when they apply, or
// it closes for one and not another.
//
// Lives in `lib/` rather than in `ui.store.ts`, where the first copy was. A viewport read is
// not store state, and exporting it from the store made it collateral in `vi.mock`: a
// wholesale mock factory REPLACES the module, so every test that stubs `ui.store` would have
// to stub this too or fail with "No 'isMobile' export is defined on the mock". That happened
// — 54 tests in `Sidebar.test.tsx` at once — and moving it here is the fix rather than
// adding the export to each mock in turn.
//
// Deliberately a function, not a hook or a cached value: it is read at the moment of a
// decision (a keypress, a navigation), never rendered from, so there is nothing to subscribe
// to and no resize listener to leak.

import { useSyncExternalStore } from 'react';

/** Tailwind's `md` breakpoint. Governs MODAL semantics — the task detail panel's scrim
 *  blocks every pointer below this, so it must declare itself a dialog. See
 *  {@link useIsMobileViewport}. It no longer governs the sidebar; see
 *  {@link SIDEBAR_DOCK_BREAKPOINT_PX}. */
export const MOBILE_BREAKPOINT_PX = 768;

export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT_PX;
}

/**
 * Tailwind's `lg` breakpoint — below this the sidebar is an overlay DRAWER; at or above it
 * docks as a persistent 260px column that pushes the content (GHE #801).
 *
 * It used to dock at `md` (768), and that was measured as the worst width in the product: the
 * List view's row gained the assignee column at exactly the breakpoint the layout took 260px
 * away from it, leaving 508px of row for 580px of fixed columns. The task title collapsed to
 * 0px and the page scrolled sideways. A 260px rail on a 768px window is 34% of the screen.
 *
 * ⚠️ Deliberately a SECOND constant rather than moving `MOBILE_BREAKPOINT_PX` to 1024. That
 * one also drives the task detail panel's modal semantics (`role="dialog"` + `aria-modal`
 * below it), and widening that to 1024 would silently make a non-modal side panel announce
 * itself as a modal on every laptop — an unrelated a11y change riding along on a layout fix.
 */
export const SIDEBAR_DOCK_BREAKPOINT_PX = 1024;

/** True when the sidebar is an overlay drawer rather than a docked column. The drawer's three
 *  dismissal routes (backdrop tap, Escape, choosing a destination) all read THIS, so they
 *  cannot disagree about when they apply. */
export function isSidebarUndocked(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < SIDEBAR_DOCK_BREAKPOINT_PX;
}

/**
 * Live, resize-aware companion to `isMobileViewport()` (GHE #669).
 *
 * The comment above says this module is deliberately NOT a hook, and that reasoning still
 * holds for `isMobileViewport()`: it answers a question at the instant of a decision. This is
 * for the other case — when the RENDERED OUTPUT depends on the breakpoint and must stay
 * correct across a resize. The task detail panel is the first: below `md` its scrim blocks
 * every pointer, so it is a modal and must SAY so (`role="dialog"` + `aria-modal`), while at
 * `md` and up the scrim is `pointer-events-none` and it is a genuine non-modal side panel
 * (`role="complementary"`). ARIA that contradicts behaviour is worse than either alone, and a
 * value read once at mount would start lying the moment the window changed.
 *
 * `useSyncExternalStore` rather than `useState` + a resize listener: it cannot tear during a
 * concurrent render, and it gives the SSR/no-window path an explicit answer instead of a
 * hydration mismatch.
 */
export function useIsMobileViewport(): boolean {
  return useSyncExternalStore(subscribeToViewport, isMobileViewport, () => false);
}

function subscribeToViewport(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  // `matchMedia` rather than `resize`: it fires only when the breakpoint is actually CROSSED,
  // not on every pixel of a drag.
  const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
  mql.addEventListener('change', onChange);
  return (): void => mql.removeEventListener('change', onChange);
}
